import React, { useState, useRef, useEffect } from 'react';
import Dialog from '../../../../../../../../../BaseComponents/Dialog/EDDialog';
import EkonomiskAnalysExportLayoutSettings, {
  PrintMode,
} from '../../../EkonomiskAnalysExport/Components/EkonomiskAnalysExportLayoutSettings/EkonomiskAnalysExportLayoutSettings';
import { IEkonomiskAnalysChartPDF } from '../../../../../../../../../Components/ExportToPDF/ExportToPDFArgumentTypes';
import { Chart } from 'highcharts';
import { ChartType } from '../../../../Queries/EkonomiskAnalysChartQuery/EkonomiskAnalysChartQuery';
import { IAnalysChartSettings } from '../../EkonomiskAnalysChartModal';
import { convertSvgStringToBase64PNG } from '../../../../Utils/chartUtils';
import { tryConvertUIUrvalToTEDUrval } from '../../../../../../../../../Components/Urval/Utils/convertUIUrvalToTEDUrval';
import DDINBase64 from '../../../../../../../../../BaseComponents/Charts/Utils/DDINBase64';
import ExportToPDF, {
  ExportToPDFFnType,
} from '../../../../../../../../../Components/ExportToPDF/ExportToPDF';
import Logger from '../../../../../../../../../Utils/Logger';
import EDOverlay from '../../../../../../../../../BaseComponents/Overlay/EDOverlay';
import { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ekonomiskAnalysDatesToDateString } from '../../../../../../../../../Utils/dateUtils';
import { IsPeriodLockedFnType } from '../../../../../../../../../Components/BokfaringsarActions/BokforingsarActions';
import EDAvenyProgress from '../../../../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import { getComparisonString } from '../../../../Utils/analysUtils';

interface IProps {
  open: boolean;
  close: () => void;
  chart: Chart | undefined;
  chartType: ChartType;
  chartSettings: IAnalysChartSettings;
  isPeriodLocked: IsPeriodLockedFnType;
  forceHelarsjamforelse: boolean;
}

const useStyles = makeStyles()((theme: Theme) => ({
  savingOverlay: {
    right: 'calc(50% - 20px)',
    bottom: 'calc(50% - 20px)',
  },
}));

const defaultPrintMode = PrintMode.portrait;

/**
 * En dialog med inställningar för hur utskrifter av graf ska göras.
 */
const EkonomiskAnalysPrintChartPDFModal: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const { close } = props;
  const { classes } = useStyles();
  const [printMode, setPrintMode] = useState(defaultPrintMode);
  const [loading, setLoading] = useState(false);

  const prevLoading = useRef(loading);

  useEffect(() => {
    if (loading === false && prevLoading.current === true) {
      close();
    }

    prevLoading.current = loading;
  }, [loading, close]);

  const printChart = (
    exportChartToPDF: ExportToPDFFnType<IEkonomiskAnalysChartPDF>
  ) => {
    if (props.chart) {
      const titleText =
        props.chartType === 'pie'
          ? props.chart.options.title != null
            ? props.chart.options.title.text
            : undefined
          : undefined;

      const portraitAndPie =
        printMode === PrintMode.portrait && props.chartType === 'pie';

      let svg = props.chart.getSVG({
        chart: {
          width: portraitAndPie ? 700 : 1000,
          height: portraitAndPie ? 600 : 600,
          style: { fontFamily: 'D-DIN' },
        },
        title: { text: titleText },
      });

      svg = svg.replace(
        /<\/svg/,
        `<style>@font-face {font-family: "D-DIN";src:url(data:application/font-woff;charset=utf-8;base64,${DDINBase64};}</style>$&`
      );

      const analys = props.chartSettings.analys;

      convertSvgStringToBase64PNG(svg, 2)
        .then(({ imageData, width, height }) => {
          exportChartToPDF(
            {
              reportName: 'EkonomiskAnalysChart',
              fileName: analys.name,
              kodbeh_id: props.chartSettings.kodbehorighetId,
              tidsintervall: {
                fromYear: analys.dates.from.year,
                fromMonth: analys.dates.from.month,
                fromDay: analys.dates.from.day,
                toYear: analys.dates.to.year,
                toMonth: analys.dates.to.month,
                toDay: analys.dates.to.day,
              },
              anvurval_selection: tryConvertUIUrvalToTEDUrval(analys.urval),
              grundurval_id: props.chartSettings.grundurvalId,
              detaljurval_selection: tryConvertUIUrvalToTEDUrval(
                analys.detaljUrval
              ),
              force_helarsjamforelse: true, // TODO behövs detta i charts?
              includeEjBokfort: analys.other.includeEjBokfordaJournaler,
              imageData,
              width,
              height,
              analys: {
                force_helarsjamforelse: true,
                name: analys.name,
                columns: analys.columns.map(col => col.data),
                sums: analys.sums,
                rubrik: analys.name,
                kategori: analys.category || 'Standard',
              },
              orientation: printMode,
              base: analys.other.denomination,
              jamforelsetal: getComparisonString(
                analys.dates,
                analys.isInterval,
                analys.dateInputType,
                props.forceHelarsjamforelse
              ),
              fromPerString: ekonomiskAnalysDatesToDateString(
                analys.dates,
                analys.dateInputType,
                false
              ),
              tomPerString: analys.isInterval
                ? ekonomiskAnalysDatesToDateString(
                    analys.dates,
                    analys.dateInputType,
                    true
                  )
                : null,
              periodLast: props.isPeriodLocked(
                analys.dates.from.year,
                analys.dates.from.month,
                analys.dates.to.month,
                analys.dateInputType
              ),
            },
            setLoading
          );
        })
        .catch(err => {
          setLoading(false);
          Logger.error(err);
        });
    }
  };

  return (
    <ExportToPDF<IEkonomiskAnalysChartPDF>>
      {exportChartToPDF => (
        <Dialog
          open={props.open}
          onClose={props.close}
          title="Skriv ut graf"
          footerActions={[
            {
              label: 'Avbryt',
              onClick: () => {
                props.close();
              },
            },
            {
              label: loading ? 'Skriver ut' : 'Skriv ut',
              onClick: () => {
                printChart(exportChartToPDF);
              },
              buttonProps: {
                disabled: loading,
              },
            },
          ]}
          onExited={() => setPrintMode(defaultPrintMode)}
        >
          <EDOverlay
            overlay={loading && <EDAvenyProgress />}
            classes={{ overlay: classes.savingOverlay }}
          >
            <EkonomiskAnalysExportLayoutSettings
              printMode={printMode}
              setPrintMode={setPrintMode}
              landscapeDisabled={loading}
              portraitDisabled={loading}
            />
          </EDOverlay>
        </Dialog>
      )}
    </ExportToPDF>
  );
};

export default EkonomiskAnalysPrintChartPDFModal;
